import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { PermissionGuard } from 'src/routes/components';

import { useWhitelist } from './hooks/useWhitelist';
import type { TranslateFn, WhitelistDraft } from './types';
import {
  buildReplacePayload,
  createEmptyDraft,
  getErrorMessage,
  isWhitelistEqual,
  toDrafts,
} from './utils';

const I18N_PREFIX = 'module.worldCup.whitelist';

const tWL = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();

  const { items, loading, saving, refetch, replace } = useWhitelist();

  const [drafts, setDrafts] = useState<WhitelistDraft[]>([]);
  const [originalDrafts, setOriginalDrafts] = useState<WhitelistDraft[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const next = toDrafts(items);
    setDrafts(next);
    setOriginalDrafts(next);
  }, [items]);

  const isDirty = useMemo(
    () => !isWhitelistEqual(drafts, originalDrafts),
    [drafts, originalDrafts]
  );

  const duplicateKeys = useMemo(() => {
    const counts = new Map<string, number>();
    drafts.forEach((row) => {
      const key = row.userId.trim();
      if (!key) return;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return new Set(
      Array.from(counts.entries())
        .filter(([, n]) => n > 1)
        .map(([k]) => k)
    );
  }, [drafts]);

  const isInvalid = useMemo(
    () => drafts.some((row) => !row.userId.trim()) || duplicateKeys.size > 0,
    [drafts, duplicateKeys]
  );

  const filteredDrafts = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return drafts;
    return drafts.filter(
      (row) =>
        row.userId.toLowerCase().includes(keyword) || row.address.toLowerCase().includes(keyword)
    );
  }, [drafts, search]);

  const handleAdd = useCallback(() => {
    setDrafts((prev) => [createEmptyDraft(), ...prev]);
  }, []);

  const handleRowChange = useCallback((localId: string, patch: Partial<WhitelistDraft>) => {
    setDrafts((prev) => prev.map((row) => (row.localId === localId ? { ...row, ...patch } : row)));
  }, []);

  const handleDelete = useCallback((localId: string) => {
    setDrafts((prev) => prev.filter((row) => row.localId !== localId));
  }, []);

  const handleDiscard = useCallback(() => {
    if (!window.confirm(tWL(t, 'confirm.discard'))) return;
    setDrafts(originalDrafts);
  }, [originalDrafts, t]);

  const handleSave = useCallback(async () => {
    if (isInvalid) {
      notify(tWL(t, 'error.invalid'), { type: 'warning' });
      return;
    }
    try {
      await replace(buildReplacePayload(drafts));
      await refetch();
      notify(tWL(t, 'notify.saveSuccess'), { type: 'success' });
    } catch (err) {
      notify(getErrorMessage(err) || tWL(t, 'notify.saveFailed'), { type: 'error' });
    }
  }, [drafts, isInvalid, notify, refetch, replace, t]);

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tWL(t, 'title')}
        links={[{ name: t('resource.worldCup') }, { name: t('resource.worldCupWhitelist') }]}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tWL(t, 'description')}
      </Typography>

      {loading && !drafts.length ? (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : (
        <Card sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6">{tWL(t, 'list.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {tWL(t, 'list.desc', { count: drafts.length })}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={handleAdd}
              >
                {tWL(t, 'actions.add')}
              </Button>
              {/* <TextField
                size="small"
                placeholder={tWL(t, 'list.searchPlaceholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280 }}
              /> */}
            </Stack>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tWL(t, 'list.col.user')}</TableCell>
                  {/* <TableCell>{tWL(t, 'list.col.reason')}</TableCell> */}
                  <TableCell align="right" sx={{ width: 120 }}>
                    {tWL(t, 'list.col.action')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDrafts.length ? (
                  filteredDrafts.map((row) => {
                    const userIdKey = row.userId.trim();
                    const missing = !userIdKey;
                    const duplicate = !missing && duplicateKeys.has(userIdKey);
                    const userIdHelper = missing
                      ? t('ra.validation.required')
                      : duplicate
                        ? tWL(t, 'error.duplicate')
                        : undefined;
                    const rowInvalid = missing || duplicate;

                    return (
                      <TableRow key={row.localId} hover>
                        <TableCell sx={{ minWidth: 240 }}>
                          <TextField
                            size="small"
                            fullWidth
                            required
                            value={row.userId}
                            error={rowInvalid}
                            helperText={userIdHelper}
                            placeholder={tWL(t, 'form.userId')}
                            onChange={(event) =>
                              handleRowChange(row.localId, { userId: event.target.value })
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <PermissionGuard
                            anyOf={[
                              { resource: 'world-cup-whitelist', action: 'write' },
                              { resource: 'world-cup-user-vouchers', action: 'write' },
                            ]}
                            fallback={<></>}
                            loadingFallback={<></>}
                          >
                            <Tooltip title={tWL(t, 'actions.remove')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(row.localId)}
                              >
                                <Iconify icon="eva:trash-2-outline" width={18} />
                              </IconButton>
                            </Tooltip>
                          </PermissionGuard>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Stack alignItems="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {tWL(t, 'list.empty')}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {isDirty ? <SaveBar loading={saving} onDiscard={handleDiscard} onSave={handleSave} /> : null}
    </DashboardContent>
  );
};

const SaveBar = memo(
  ({
    loading,
    onDiscard,
    onSave,
  }: {
    loading: boolean;
    onDiscard: () => void;
    onSave: () => void;
  }) => {
    const t = useTranslate();

    return (
      <Paper
        elevation={8}
        sx={{
          position: 'sticky',
          bottom: 16,
          mt: 3,
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {tWL(t, 'saveBar.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tWL(t, 'saveBar.desc')}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" disabled={loading} onClick={onDiscard}>
            {tWL(t, 'actions.discard')}
          </Button>
          <PermissionGuard
            anyOf={[
              { resource: 'world-cup-whitelist', action: 'write' },
              { resource: 'world-cup-user-vouchers', action: 'write' },
            ]}
            fallback={<></>}
            loadingFallback={<></>}
          >
            <Button variant="contained" disabled={loading} onClick={onSave}>
              {loading ? t('common.saving') : tWL(t, 'actions.save')}
            </Button>
          </PermissionGuard>
        </Stack>
      </Paper>
    );
  }
);

SaveBar.displayName = 'SaveBar';
